// src/components/layout/PermissionGuard.jsx
// Route/component gating based on the user's organization role permissions.
// Similar to FeatureGuard but checks permission keys instead of solution IDs.

import { Navigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { useUser } from '@/contexts/UserContext';

export function PermissionGuard({
  children,
  requiredPermission,
  requiredPermissions = [],
  requireAny = false,
  fallback = null,
  redirectTo = '/'
}) {
  const { can, canAny, canAll, loading, isPreviewing } = usePermissions();
  const { user } = useUser();

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Admin users bypass all permission checks — unless previewing another role
  if (!isPreviewing && (user?.role === 'Admin' || user?.role === 'admin')) {
    return children;
  }

  // Check single required permission
  if (requiredPermission && !can(requiredPermission)) {
    return fallback || <Navigate to={redirectTo} replace />;
  }

  // Check multiple required permissions
  if (requiredPermissions.length > 0) {
    const hasAccess = requireAny
      ? canAny(requiredPermissions)
      : canAll(requiredPermissions);

    if (!hasAccess) {
      return fallback || <Navigate to={redirectTo} replace />;
    }
  }

  return children;
}
